import React, { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import {
    Box,
    Grid,
    Typography,
    Button,
    Paper,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Avatar,
    LinearProgress,
    IconButton,
    Divider
} from "@mui/material";
import {
    AccessTime as ClockIcon,
    CalendarToday as CalendarIcon,
    Timer as TimerIcon,
    Fingerprint as FingerprintIcon,
    Schedule as ScheduleIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import axios from "axios";
import { makeApiRequest } from '../../../utils/api';
import { trimSeconds, formatTimeToHoursMinutes, formatDuration, formatDate } from '../../../helpers/timeHelper';

export const DailyAttendance = () => {
    const [attendanceData, setAttendanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAttendanceData = async () => {

            setLoading(true);
            setError(null);

            try {
                const response = await makeApiRequest('post', '/api/attendance/punch-in', {
                    token: localStorage.getItem('authToken'),
                    "action": "get"
                });

                if (response.status === 'success') {
                    setAttendanceData(response.data);
                } else {
                    throw new Error('Failed to fetch data');
                }
            } catch (err) {
                console.error('API Error:', err);
                setError(err.response?.data?.message || 'Error fetching data');
            } finally {
                setLoading(false);
            }

        };

        fetchAttendanceData();

    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((oldProgress) => {
                if (oldProgress === 100) {
                    return 0;
                }
                const diff = Math.random() * 10;
                return Math.min(oldProgress + diff, 100);
            });
        }, 500);

        return () => {
            clearInterval(timer);
        };
    }, []);

    const getStatusColor = (delayTime) => {
        const delay = parseInt(delayTime);
        if (delay === 0) return "success";
        if (delay <= 15) return "warning";
        return "error";
    };

    const getTimePercent = () => {
        if (!attendanceData) return 0;
        const [hours, minutes] = attendanceData.officeTime.split(':');
        const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
        return (totalMinutes / (24 * 60)) * 100;
    };

    const handleSubmit = async (event) => {

        setLoading(true);
        setError(null);

        try {
            const response = await makeApiRequest('post', '/api/attendance/punch-in', {
                token: localStorage.getItem('authToken'),
                "action": event.target.value
            });

            if (response.status === 'success') {
                window.location.reload();
                // setAttendanceData(response.data);
            } else {
                throw new Error('Failed to fetch data');
            }
        } catch (err) {
            console.error('API Error:', err);
            setError(err.response?.data?.message || 'Error fetching data');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Box sx={{
            width: "100%",
            minHeight: "90vh",
            bgcolor: "#f8fafc",
            p: { xs: 2, sm: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxSizing: 'border-box'
        }}>
            <Card sx={{
                width: "100%",
                maxWidth: { xs: '100%', sm: "75%" },
                borderRadius: 4,
                boxShadow: "0 4px 24px 0 rgba(34,41,47,0.1)",
                overflow: "visible"
            }}>
                {loading ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Header Section */}
                        <Box sx={{
                            p: { xs: 2, sm: 4 },
                            background: "linear-gradient(135deg, #6B8DD6 0%, #8E37D7 100%)",
                            background: "linear-gradient(135deg, #6B8DD6 0%, #8E37D7 100%)",
                            color: "white",
                            borderRadius: "16px 16px 0 0",
                            position: "relative"
                        }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="h5" fontWeight="bold" sx={{
                                    fontSize: {
                                        xs: "1rem", // For extra-small screens (mobile)
                                        sm: "1.5rem", // For small screens and larger (tablet)
                                    }
                                }}>
                                    Daily Attendance
                                </Typography>
                                <IconButton sx={{ color: "white" }}>
                                    <MoreVertIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                                <Avatar
                                    sx={{
                                        width: { sm: 64 },
                                        height: { sm: 64 },
                                        bgcolor: "white",
                                        color: "primary.main",
                                        fontWeight: "bold",
                                        fontSize: "1.5rem"
                                    }}
                                >
                                    {attendanceData?.name?.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="medium" sx={{
                                        fontSize: {
                                            xs: "1rem", // For extra-small screens (mobile)
                                            sm: "1.5rem", // For small screens and larger (tablet)
                                        }
                                    }}>
                                        {attendanceData?.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        Employee ID: {attendanceData?.userid}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <CardContent sx={{ p: 3 }}>
                            {/* Time Progress */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    bgcolor: "#f1f5f9",
                                    borderRadius: 3
                                }}
                            >
                                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{
                                        fontSize: {
                                            xs: ".7rem", // For extra-small screens (mobile)
                                            sm: "1.5rem", // For small screens and larger (tablet)
                                        }
                                    }}>
                                        Workday Progress
                                    </Typography>
                                    <Chip
                                        label={getStatusColor(attendanceData?.delay) === "success" ? "On Time" : "Delayed"}
                                        color={getStatusColor(attendanceData?.delay)}
                                        size="small"
                                    />
                                </Box>
                                {/* <LinearProgress 
                  variant="determinate" 
                  value={getTimePercent()}
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    bgcolor: "rgba(0,0,0,0.1)",
                    mb: 2
                  }}
                /> */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} sx={{ marginBottom: { xs: '10px', sm: '0' } }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <CalendarIcon color="primary" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Date
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight="medium" mt={1}>
                                            {attendanceData?.date}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ marginBottom: { xs: '10px', sm: '0' } }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <TimerIcon color="primary" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Office Time
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight="medium" mt={1}>
                                            {attendanceData?.
                                                start_time
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ marginBottom: { xs: '10px', sm: '0' } }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <ClockIcon color="primary" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                {attendanceData?.punch_out_time ? 'Punch Out Time' : (attendanceData?.punch_in_time ? 'Punch In Time' : "Current Time")}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight="medium" mt={1}>
                                            {attendanceData?.punch_out_time ? attendanceData?.punch_out_time : (attendanceData?.punch_in_time ? attendanceData?.punch_in_time : attendanceData?.current_time)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <ScheduleIcon color="primary" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Delay
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            mt={1}
                                            color={getStatusColor(attendanceData?.delay) + ".main"}
                                        >
                                            {formatTimeToHoursMinutes(attendanceData?.delay)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Quick Stats */}
                            <Grid container spacing={2} mb={3}>
                                <Grid item xs={12} sm={6} marginBottom={{ xs: 3, sm: 0 }}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: "primary.lighter",
                                            borderRadius: 3,
                                            height: "100%",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: "center", gap: 2 }}>
                                            <Avatar sx={{ bgcolor: "primary.main" }}>
                                                <TimerIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Working Hours
                                                </Typography>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {attendanceData?.user_working_hours ? attendanceData?.user_working_hours : '-'} <span style={{ fontWeight: 100 }}>hrs</span>
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6} marginBottom={{ xs: 3, sm: 0 }}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: "secondary.lighter",
                                            borderRadius: 3,
                                            height: "100%",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, alignItems: "center", gap: 2 }}>
                                            <Avatar sx={{ bgcolor: "secondary.main" }}>
                                                <FingerprintIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Status
                                                </Typography>
                                                <Typography variant={{ xs: "h4", sm: 'h6' }} fontWeight="bold">
                                                    {attendanceData?.attendance_status}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Action Button */}
                            <Box sx={{ textAlign: "center" }}>
                                {attendanceData?.punch_out_time == null &&
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<FingerprintIcon />}
                                        sx={{
                                            px: { xs: 4, sm: 6 },
                                            py: 1.5,
                                            borderRadius: 3,
                                            textTransform: "none",
                                            fontSize: "1rem",
                                            background: "linear-gradient(135deg, #6B8DD6 0%, #8E37D7 100%)",
                                            boxShadow: "0 8px 16px rgba(142, 55, 215, 0.2)",
                                            '&:hover': {
                                                background: "linear-gradient(135deg, #5d7cc0 0%, #7c2ebf 100%)",
                                            }
                                        }}
                                        onClick={handleSubmit}
                                        value={attendanceData?.punch_in_time ? 'punch_out' : 'punch_in'}
                                    >
                                        {attendanceData?.punch_in_time ? 'Punch Out' : (attendanceData?.current_time ? 'Punch In' : '')}
                                    </Button>
                                }
                            </Box>
                        </CardContent>
                    </>
                )}
            </Card>
        </Box>
    );
};

export default DailyAttendance;